import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Alert } from '@syneto/compass-react';
import {
	COMPLIANCE_VERDICT_STATUS,
	GET_COMPLIANCE_VERDICTS_DEFAULT_QUERY_PARAMS
} from '../../../../constants/compliance-verdicts';
import { getComponentTypes } from '../../../../data/hardwareInventory/actions';
import {
	createComplianceVerdict,
	getComplianceVerdicts,
	grantComplianceVerdict,
	hideComplianceVerdictFormModal,
	hideComplianceVerdictGrantModal,
	hideComplianceVerdictRevokeModal,
	isOverrideWriteAccepted,
	revokeComplianceVerdict,
	showComplianceVerdictFormModal,
	showComplianceVerdictGrantModal,
	showComplianceVerdictRevokeModal
} from '../../../../data/complianceVerdict/actions';
import ComplianceVerdictFormModal from './complianceVerdictFormModal';
import ComplianceVerdictGrantModal from './complianceVerdictGrantModal';
import ComplianceVerdictRevokeModal from './complianceVerdictRevokeModal';
import ComplianceVerdictsSearchBar from './complianceVerdictsSearchBar';
import ComplianceVerdictsTable from './complianceVerdictsTable';

const AdminComplianceVerdicts = (props) => {
	const {
		verdicts,
		pagination,
		loadedSerialNumber,
		verdictsLoading,
		verdictsError,
		saving,
		showFormModal,
		verdictToGrant,
		verdictToRevoke,
		componentTypes,
		componentTypesLoading,
		componentTypesError,
		grantedBy,
		getComplianceVerdicts,
		getComponentTypes,
		createComplianceVerdict,
		grantComplianceVerdict,
		revokeComplianceVerdict,
		openFormModal,
		hideFormModal,
		showGrantModalFor,
		hideGrantModal,
		showRevokeModalFor,
		hideRevokeModal
	} = props;

	/*
	 * Two pieces of state, deliberately separate: `serialInput` is the draft the box renders and
	 * `queryParams.serialNumber` is what the table is actually showing. Editing the box must not
	 * change what the page claims, so only Search commits the draft.
	 */
	const [serialInput, setSerialInput] = useState('');
	const [queryParams, setQueryParams] = useState(GET_COMPLIANCE_VERDICTS_DEFAULT_QUERY_PARAMS);

	/*
	 * A write outlives its request: it goes on to poll a job for up to a minute. Navigating away
	 * mid-poll has to stop it, otherwise it dispatches into an unmounted page and pushes a
	 * notification about work nobody is watching any more.
	 */
	const abortRef = useRef(null);

	useEffect(() => {
		abortRef.current = new AbortController();

		return () => {
			abortRef.current?.abort();
		};
	}, []);

	/*
	 * The catalog powers the component type picker and the scope labels in the table. Fetched
	 * unconditionally rather than reused-if-present: componentTypesLoading starts true, so a guard
	 * that skips the fetch would leave the picker spinning forever on a fresh store.
	 */
	useEffect(() => {
		getComponentTypes();
	}, []);

	/*
	 * Fires on mount too: a blank serial lists every appliance.
	 */
	useEffect(() => {
		getComplianceVerdicts(queryParams);
	}, [queryParams]);

	const writeOptions = (params) => {
		return { queryParams: params ?? queryParams, signal: abortRef.current?.signal };
	};

	const onSearch = () => {
		setQueryParams((current) => {
			return { ...current, serialNumber: serialInput.trim(), start: 0 };
		});
	};

	const onStatusChange = (status) => {
		setQueryParams((current) => {
			return { ...current, status: status, start: 0 };
		});
	};

	const onPaginationChange = (start, limit) => {
		setQueryParams((current) => {
			return { ...current, start: start, limit: limit };
		});
	};

	/*
	 * The serial stays editable in the modal, so a grant can legitimately target an appliance other
	 * than the one on screen - and then the pipeline's refresh would reload the *searched* serial
	 * and the new override would be nowhere to be seen behind a success toast. Moving the page to
	 * the serial that was written, under the Active filter, is what makes the toast true.
	 *
	 * The move waits for the write to be accepted, and is skipped when it was not. Committing up
	 * front would point the table at an appliance nothing was created on - and, because the
	 * committed serial is what prefills the modal, would reinitialize the form the pipeline
	 * deliberately leaves open and wipe the very values the operator is meant to correct in place.
	 * That is not an edge case here: an unknown component type id passes the request schema and
	 * fails as a job, which is this feature's primary error path.
	 *
	 * The pipeline is still given the written serial, so the refresh it awaits reads the appliance
	 * the override landed on. Committing afterwards re-runs the queryParams effect and reads it a
	 * second time; that redundant read is the price of never committing a serial the API refused.
	 */
	const onSubmitOverride = async (values) => {
		const nextQueryParams = {
			...queryParams,
			start: 0,
			serialNumber: String(values?.serialNumber ?? '').trim(),
			status: COMPLIANCE_VERDICT_STATUS.ACTIVE
		};

		const outcome = await createComplianceVerdict(values, {
			...writeOptions(nextQueryParams),
			grantedBy: grantedBy
		});

		if (isOverrideWriteAccepted(outcome)) {
			setSerialInput(nextQueryParams.serialNumber);
			setQueryParams(nextQueryParams);
		}

		return outcome;
	};

	/*
	 * The pipeline's refresh re-reads the page on screen, so the granted row reads Active in place
	 * without a manual reload.
	 */
	const onConfirmGrant = (values) => {
		return grantComplianceVerdict(verdictToGrant.id, values, { ...writeOptions(), grantedBy: grantedBy });
	};

	const onConfirmRevoke = () => {
		return revokeComplianceVerdict(verdictToRevoke.id, { ...writeOptions(), revokedBy: grantedBy });
	};

	return (
		<div id="compliance-verdicts" className="p-4 bg-white w-100">
			<div className="col-12">
				{verdictsError && <Alert variant="danger">{verdictsError}</Alert>}
				{componentTypesError && <Alert variant="danger">{componentTypesError}</Alert>}

				{/*
				  * `grantedBy` and `revokedBy` are NOT NULL audit columns the API rejects when blank,
				  * and the profile loads asynchronously, so the session email can legitimately be
				  * missing for a moment. Blocking every write is better than a 422 whose message is
				  * about a field the operator never filled in.
				  */}
				{!grantedBy && (
					<Alert variant="warning">
						Your account details are still loading. Creating, granting and revoking overrides is unavailable until
						they arrive, because every override records who granted it.
					</Alert>
				)}

				<ComplianceVerdictsSearchBar
					serialInput={serialInput}
					committedSerialNumber={queryParams.serialNumber}
					status={queryParams.status}
					isLoading={verdictsLoading}
					isSaving={saving}
					canCreate={Boolean(grantedBy)}
					onSerialInputChange={setSerialInput}
					onSearch={onSearch}
					onStatusChange={onStatusChange}
					onCreate={openFormModal}
				/>

				<ComplianceVerdictsTable
					verdicts={verdicts}
					pagination={pagination}
					componentTypes={componentTypes}
					componentTypesLoading={componentTypesLoading}
					isLoading={verdictsLoading}
					hasError={Boolean(verdictsError)}
					isSaving={saving}
					statusFilter={queryParams.status}
					loadedSerialNumber={loadedSerialNumber}
					onPaginationChange={onPaginationChange}
					onGrantVerdict={showGrantModalFor}
					onRevokeVerdict={showRevokeModalFor}
				/>

				{showFormModal && (
					<ComplianceVerdictFormModal
						serialNumber={queryParams.serialNumber}
						componentTypes={componentTypes}
						componentTypesLoading={componentTypesLoading}
						grantedBy={grantedBy}
						submitting={saving}
						onSubmitOverride={onSubmitOverride}
						onCancel={hideFormModal}
					/>
				)}

				{verdictToGrant && (
					<ComplianceVerdictGrantModal
						verdict={verdictToGrant}
						componentTypes={componentTypes}
						componentTypesLoading={componentTypesLoading}
						grantedBy={grantedBy}
						submitting={saving}
						onConfirm={onConfirmGrant}
						onCancel={hideGrantModal}
					/>
				)}

				{verdictToRevoke && (
					<ComplianceVerdictRevokeModal
						verdict={verdictToRevoke}
						componentTypes={componentTypes}
						componentTypesLoading={componentTypesLoading}
						revokedBy={grantedBy}
						isSaving={saving}
						onConfirm={onConfirmRevoke}
						onCancel={hideRevokeModal}
					/>
				)}
			</div>
		</div>
	);
};

const mapStateToProps = (state) => {
	return {
		verdicts: state.complianceVerdicts.verdicts,
		pagination: state.complianceVerdicts.pagination,
		loadedSerialNumber: state.complianceVerdicts.loadedSerialNumber,
		verdictsLoading: state.complianceVerdicts.verdictsLoading,
		verdictsError: state.complianceVerdicts.verdictsError,
		saving: state.complianceVerdicts.saving,
		showFormModal: state.complianceVerdicts.showFormModal,
		verdictToGrant: state.complianceVerdicts.verdictToGrant,
		verdictToRevoke: state.complianceVerdicts.verdictToRevoke,
		/*
		 * Read from the hardware inventory slice rather than fetched into this one: it is the same
		 * unpaginated endpoint, and a second copy could disagree with the fleet dashboard about
		 * which component types exist.
		 */
		componentTypes: state.hardwareInventory.componentTypes,
		componentTypesLoading: state.hardwareInventory.componentTypesLoading,
		componentTypesError: state.hardwareInventory.componentTypesError,
		/*
		 * The identity recorded on every grant and revoke. Taken from the session rather than typed,
		 * so an operator cannot attribute a compliance waiver to someone else.
		 */
		grantedBy: state.user?.user?.account?.email ?? ''
	};
};

const mapDispatchToProps = (dispatch) => {
	return {
		getComplianceVerdicts: (queryParams) => { return dispatch(getComplianceVerdicts(queryParams)); },
		getComponentTypes: () => { return dispatch(getComponentTypes()); },
		createComplianceVerdict: (values, options) => { return dispatch(createComplianceVerdict(values, options)); },
		grantComplianceVerdict: (id, values, options) => { return dispatch(grantComplianceVerdict(id, values, options)); },
		revokeComplianceVerdict: (id, options) => { return dispatch(revokeComplianceVerdict(id, options)); },
		openFormModal: () => { return dispatch(showComplianceVerdictFormModal()); },
		hideFormModal: () => { return dispatch(hideComplianceVerdictFormModal()); },
		showGrantModalFor: (verdict) => { return dispatch(showComplianceVerdictGrantModal(verdict)); },
		hideGrantModal: () => { return dispatch(hideComplianceVerdictGrantModal()); },
		showRevokeModalFor: (verdict) => { return dispatch(showComplianceVerdictRevokeModal(verdict)); },
		hideRevokeModal: () => { return dispatch(hideComplianceVerdictRevokeModal()); }
	};
};

export default connect(mapStateToProps, mapDispatchToProps)(AdminComplianceVerdicts);
